import { SwitchSearch } from '@aibees/elephant/components';
import { BREADCRUMBS_CONFIG } from '@aibees/elephant/constants';
import { useSidebarOptions } from '@aibees/elephant/constants/dashboard-sidebar-config';
import { SearchBrowseDataProvider } from '@aibees/elephant/context/search-browse-data-provider';
import { SearchParamsSourceProvider } from '@aibees/elephant/context/search-params-source-provider';
import { SearchParamsUrlSync } from '@aibees/elephant/context/search-params-url-sync';
import { SearchTableScopeRouteProvider } from '@aibees/elephant/context/search-table-scope';
import { PinnedFiltersProvider } from '@aibees/elephant/feature/more-filters/context';
import { MoreFilters } from '@aibees/elephant/feature/search-filters/components/more-filters';
import { SearchFiltersInput } from '@aibees/elephant/feature/search-filters/components/search-filters-input';
import { useFilterValues } from '@aibees/elephant/hooks';
import { useAccountMenuOptions } from '@aibees/elephant/hooks/use-account-menu-options';
import { ROUTES } from '@aibees/elephant/router';
import { usePrefetchSearchPresets } from './hooks';
import {
  HeaderBreadcrumbs,
  Icon,
  LoadingScreen,
} from '@aibees/shared/components';
import { GlobalNotificationBar } from '@aibees/shared/features';
import { DashboardLayout } from '@aibees/shared/layouts';
import { Badge } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import {
  lazy,
  memo,
  type PropsWithChildren,
  type ReactNode,
  Suspense,
  useMemo,
} from 'react';
import { SearchLayoutActionList } from '../../components/search-layout-action-list';
import { SearchLayoutHeaderTitle } from '../../components/search-layout-header-title';
import { ViewRecordsHeaderAction } from '../../components/view-record-button/view-records-header-action';

const SearchFilters = lazy(() =>
  import(
    /* vitePreload: true */ '@aibees/elephant/feature/search-filters'
  ).then((module) => ({
    default: module.SearchFilters,
  }))
);

const BREADCRUMB_CONFIG = {
  titles: BREADCRUMBS_CONFIG,
  routes: ROUTES,
};

const LOGO_SX: SxProps<Theme> = { height: (p) => p.spacing(3) };

const BADGE_ANCHOR_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};

const NAV_HEADER_SX = { width: '100%' };
const NAV_SX = { paddingBottom: 0 };
const NAV_CONFIG_SX = { sx: { py: 0 } };

// ── Module-level stable JSX elements ──
// These never change so they are created once, eliminating per-render allocations.

const filterPanelPrepend: ReactNode = (
  <Suspense fallback={<LoadingScreen />}>
    <SearchFilters />
  </Suspense>
);

const topElement: ReactNode = <GlobalNotificationBar />;
const breadcrumb: ReactNode = <HeaderBreadcrumbs config={BREADCRUMB_CONFIG} />;
const navBeforeElement: ReactNode = <SwitchSearch />;
const navSearchElement: ReactNode = <SearchFiltersInput />;
const navCloseIcon: ReactNode = (
  <Icon name="left-panel-close" size={22} color={'grey.500'} />
);
const navAction: ReactNode = <SearchLayoutActionList />;
const navTitle = { open: <SearchLayoutHeaderTitle />, close: null };
const navFooter: ReactNode = <MoreFilters />;

/**
 * Tiny component that isolates the `useFilterValues` subscription
 * so that SearchLayoutInner does NOT re-render on every param change.
 */
const NavOpenBadge = memo(function NavOpenBadge() {
  const values = useFilterValues();
  return (
    <Badge
      variant="dot"
      color="primary"
      invisible={!values?.length}
      anchorOrigin={BADGE_ANCHOR_ORIGIN}
    >
      <Icon name="left-panle-open" size={18} color={'grey.500'} />
    </Badge>
  );
});

const navOpenIcon: ReactNode = <NavOpenBadge />;

const SearchLayoutInner = memo(function SearchLayoutInner({
  children,
}: PropsWithChildren) {
  usePrefetchSearchPresets();
  const accountMenuOptions = useAccountMenuOptions();
  const sidebarItems = useSidebarOptions();

  const options = useMemo(
    () => ({
      topElement,
      showLoadingBar: true,
      header: {
        accountMenu: accountMenuOptions,
        breadcrumb,
        logoSx: LOGO_SX,
        hideNavVertical: false,
        topRightSlot: <ViewRecordsHeaderAction />,
      },
      sidebar: sidebarItems,
      navigation: {
        header: {
          beforeElement: navBeforeElement,
          searchElement: navSearchElement,
          closable: true as const,
          icon: {
            open: navOpenIcon,
            close: navCloseIcon,
            action: navAction,
          },
          title: navTitle,
          sx: NAV_HEADER_SX,
        },
        sx: NAV_SX,
        config: NAV_CONFIG_SX,
        prepend: filterPanelPrepend,
        footer: navFooter,
      },
    }),
    [accountMenuOptions, sidebarItems]
  );

  return (
    <PinnedFiltersProvider>
      <DashboardLayout options={options}>{children}</DashboardLayout>
    </PinnedFiltersProvider>
  );
});

function SearchLayout({ children }: PropsWithChildren) {
  return (
    <SearchTableScopeRouteProvider>
      <SearchParamsUrlSync>
        <SearchParamsSourceProvider>
          <SearchBrowseDataProvider>
            <SearchLayoutInner>{children}</SearchLayoutInner>
          </SearchBrowseDataProvider>
        </SearchParamsSourceProvider>
      </SearchParamsUrlSync>
    </SearchTableScopeRouteProvider>
  );
}

export default memo(SearchLayout);
